import { useEffect, useState } from 'react'
import { Navigate } from 'react-router'
import { useAuth, useRole, useGymId, useStaffPermissions } from '@structural-climbing/auth'
import type { StaffPermissions } from '@structural-climbing/auth'
import { inviteReturn } from '../features/team/inviteReturn'

// Order matters: the first permission a staff member holds decides where they
// land. Keep it in sync with the sidebar in GymLayout, and remember that a
// staff member whose only permission is missing from this list is sent to
// /unauthorized — which is how a trainer, who holds `my_members` alone, was
// locked out of the app entirely.
const STAFF_ROUTE_ORDER: { key: keyof StaffPermissions; path: string }[] = [
  { key: 'dashboard', path: 'dashboard' },
  { key: 'check_in', path: 'check-in' },
  { key: 'my_members', path: 'my-members' },
  { key: 'members', path: 'members' },
  { key: 'requests', path: 'requests' },
  { key: 'plans', path: 'plans' },
  { key: 'routes', path: 'routes' },
  { key: 'statistics', path: 'statistics' },
  { key: 'invoices', path: 'invoices' },
  { key: 'products', path: 'products' },
  { key: 'calendar', path: 'calendar' },
]

interface GymInfoResponse {
  slug: string
}

export function DefaultRedirectPage() {
  const { isAuthenticated, isLoading, error } = useAuth()
  const role = useRole()
  const gymId = useGymId()
  const staffPermissions = useStaffPermissions()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [slug, setSlug] = useState<string | null>(null)

  useEffect(() => {
    if (!gymId) return
    void fetch(`${apiUrl}/gym-info/${gymId}`)
      .then((res) => (res.ok ? (res.json() as Promise<GymInfoResponse>) : null))
      .then((data) => {
        if (data?.slug) setSlug(data.slug)
      })
      .catch(() => {})
  }, [gymId, apiUrl])

  if (isLoading) return null
  if (!isAuthenticated) {
    // Just signed out to accept an invite sent to another address: back to the
    // invite, not to a login form that would sign the old account in again.
    // AcceptInvitePage clears the stored link when it mounts.
    //
    // Not when Auth0 has just refused the login, though: the invitee signed in
    // as an account the invite was not sent to, and has to be told so. The
    // login page says it, and its sign-out comes back through here error-free.
    const invite = error ? null : inviteReturn()
    return <Navigate to={invite ?? '/login'} replace />
  }

  if (role === 'gym_owner') {
    if (!slug) return null
    return <Navigate to={`/${slug}/dashboard`} replace />
  }

  if (role === 'gym_staff') {
    if (staffPermissions === null || !slug) return null
    const first = STAFF_ROUTE_ORDER.find((r) => staffPermissions[r.key] === true)
    return <Navigate to={first ? `/${slug}/${first.path}` : '/unauthorized'} replace />
  }

  return <Navigate to="/unauthorized" replace />
}
